import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";

import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import CurrencyPoundOutlinedIcon from "@mui/icons-material/CurrencyPoundOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import Header from "../components/NewUi/Header/LoginHeader";

import axios from "axios";
import { useMutation } from "@tanstack/react-query";

import PrivacyPolicyModal from "./privacyPolicy/PrivacyPolicyModal";
import SaasModal from "./privacyPolicy/SaasModel";
import EulaModal from "./privacyPolicy/eulaModal";

import styles from "./Login.module.scss";

const authClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Authorization: null,
  },
});

export default function Login() {
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showSAASModal, setShowSAASModal] = useState(false);
  const [showEulaModal, setShowEulaModal] = useState(false);

  const [privacyContent, setPrivacyContent] = useState("");
  const [saasContent, setSaasContent] = useState("");
  const [eulaContent, setEulaContent] = useState("");

  const [hasGuestRef, setHasGuestRef] = useState(false);
  const [microsoftLoginError, setMicrosoftLoginError] = useState("");

  const [email, setEmail] = useState("you@practice.com");
  const [password, setPassword] = useState("password");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const guestRef = params.get("guest_purchase_reference");
    setHasGuestRef(Boolean(guestRef));

    if (guestRef) {
      localStorage.setItem("guest_purchase_reference", guestRef);
    }
  }, []);

  const loadDoc = async (id, setter, modalSetter) => {
    setter("<p>Loading document...</p>");
    modalSetter(true);

    try {
      const { data } = await axios.get(`/document_agreement/${id}`);

      // Support the current API response (`data.document`) and older formats.
      const documentContent = [
        typeof data === "string" ? data : undefined,
        data?.document,
        data?.content,
        data?.document_content,
        data?.html_content,
        data?.data?.document,
        data?.data?.content,
        data?.data?.document_content,
        data?.data?.html_content,
        data?.legal_document?.document,
        data?.legal_document?.content,
        data?.legal_document?.document_content,
      ].find(
        (value): value is string =>
          typeof value === "string" && Boolean(value.trim()),
      );

      if (typeof documentContent !== "string" || !documentContent.trim()) {
        throw new Error("The legal document response did not contain content.");
      }

      setter(documentContent);
    } catch (error) {
      console.error(`Failed to load legal document ${id}:`, error);
      setter(
        "<p>We could not load this document. Please close the window and try again.</p>"
      );
    }
  };

  const loginDentally = useMutation({
    mutationFn: () =>
      authClient.post("/auth/get_request_auth_link", {
        provider: "dentally",
      }),
    onMutate: () => localStorage.setItem("auth_provider", "dentally"),
    onSuccess: (res) => {
      window.location.replace(res.data);
    },
  });

  const loginMicrosoft = useMutation({
    mutationFn: async () => {
      const response = await authClient.post("/auth/get_request_auth_link", {
        provider: "microsoft",
      });
      if (typeof response?.data !== "string" || !response.data.trim()) {
        throw new Error("The authorization response did not contain a URL.");
      }
      const authUrl = new URL(response.data);
      if (authUrl.protocol !== "https:" && authUrl.protocol !== "http:") {
        throw new Error("The authorization URL is invalid.");
      }
      return response.data;
    },
    onMutate: () => {
      localStorage.setItem("auth_provider", "microsoft");
      setMicrosoftLoginError("");
    },
    onSuccess: (authUrl) => {
      window.location.replace(authUrl);
    },
    onError: () => {
      setMicrosoftLoginError("We could not start Microsoft sign-in. Please try again.");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  return (
    <div className={styles.page}>
       <Header />
      <div className={styles.bgOrb1} />
      <div className={styles.bgOrb2} />
      <div className={styles.bgOrb3} />
      <div className={styles.bgOrb4} />
      <div className={styles.bgOrb5} />

      <main className={styles.main}>
        <div className={styles.contentShell}>
          <section className={styles.leftPanel}>
            <div className={styles.leftInner}>
              <h1 className={styles.heroTitle}>
                Stop Losing High Value Cases to Poor Consent Workflows
              </h1>

              <p className={styles.heroSubtitle}>
                Send 3 AI-personalised consent packs a day. Watch case acceptance
                double. Your patients finally understand,and say yes.
              </p>

              <div className={styles.visualCard}>
                <img
                  src="/tooth.gif"
                  alt="Dental model"
                  className={styles.visualImage}
                />
              </div>

              <div className={styles.featureList}>
                <div className={styles.featureItem}>
                  <div className={styles.featureIcon}>
                    <GppGoodOutlinedIcon />
                  </div>
                  <span>AI writes your consent forms in seconds not hours</span>
                </div>

                <div className={styles.featureItem}>
                  <div className={styles.featureIcon}>
                    <BoltOutlinedIcon />
                  </div>
                  <span>
                    Patients see exactly what you see,3D visuals they actually
                    understand
                  </span>
                </div>

                <div className={styles.featureItem}>
                  <div className={styles.featureIcon}>
                    <ShieldOutlinedIcon />
                  </div>
                  <span>One-click Dentally sync zero double entry</span>
                </div>
              </div>

              <div className={styles.statsRow}>
                <div className={styles.statCard}>
                  <div className={styles.statIcon}>
                    <AccessTimeOutlinedIcon />
                  </div>
                  <div className={styles.statValue}>20 min</div>
                  <div className={styles.statLabel}>Setup Time</div>
                </div>

                <div className={styles.statCard}>
                  <div className={styles.statIcon}>
                    <TrendingUpOutlinedIcon />
                  </div>
                  <div className={styles.statValue}>2x</div>
                  <div className={styles.statLabel}>Case Acceptance</div>
                </div>

                <div className={styles.statCard}>
                  <div className={styles.statIcon}>
                    <CurrencyPoundOutlinedIcon />
                  </div>
                  <div className={styles.statValue}>10x</div>
                  <div className={styles.statLabel}>ROI on Investment</div>
                </div>
              </div>

              <div className={styles.badgesRow}>
                <div className={styles.badgePill}>
                  <ShieldOutlinedIcon />
                  <span>Following GDC Standards</span>
                </div>

                <div className={styles.badgePill}>
                  <GppGoodOutlinedIcon />
                  <span>GDPR Compliant</span>
                </div>

                <div className={styles.badgePill}>
                  <LockOutlinedIcon />
                  <span>Encrypted &amp; Secure</span>
                </div>
              </div>
            </div>
          </section>

          <section className={styles.rightPanel}>
            <div className={styles.rightInner}>
              <div className={styles.loginCard}>
                <div className={styles.loginHeader}>
                  <h2>Sign In</h2>
                  <p>Your patients are waiting for clarity</p>
                </div>

                <div className={styles.miniBrand}>
                  {/* <img src="/logo.png" alt="Medicube" className={styles.miniLogo} /> */}
                </div>

                {hasGuestRef && (
                  <div className={styles.alertWrap}>
                    <Alert
                      severity="warning"
                      variant="outlined"
                      sx={{
                        borderRadius: 14,
                        borderWidth: 1.5,
                        px: 2,
                        py: 1.2,
                        "& .MuiAlertTitle-root": { fontWeight: 800, mb: 0.5 },
                      }}
                    >
                      <AlertTitle>Important notice</AlertTitle>
                      When you connect to Dentally via Medicube for the first
                      time, Dentally may show a standard warning related to
                      third-party access and data security. Read our{" "}
                      <span
                        className={styles.inlineLink}
                        onClick={() =>
                          loadDoc(3, setPrivacyContent, setShowPrivacyModal)
                        }
                        role="button"
                        tabIndex={0}
                      >
                        Privacy Policy
                      </span>
                      .
                    </Alert>
                  </div>
                )}

                <Button
                  className={styles.dentallyButton}
                  onClick={() => loginDentally.mutate()}
                  disabled={loginDentally.isPending}
                >
                  <img
                    src="/dentally-Photoroom.png"
                    alt="Dentally"
                    className={styles.dentallyLogo}
                  />
                  <span>
                    {loginDentally.isPending
                      ? "Redirecting..."
                      : "Sign In with Dentally SSO"}
                  </span>
                </Button>

                <div className={styles.recommendedText}>
                  Recommended — Single Sign-On via Dentally
                </div>

                <div className={styles.dividerWrap}>
                  <span className={styles.dividerLine} />
                  {/* <span className={styles.dividerText}>OR</span> */}
                  <span className={styles.dividerLine} />
                </div>

                {microsoftLoginError && (
                  <Alert severity="error">{microsoftLoginError}</Alert>
                )}

                <div className={styles.ssoButtons}>
                  <button
                    type="button"
                    className={styles.ssoButton}
                    disabled
                    title="Google sign-in is not connected yet"
                  >
                    <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20">
                      <path fill="#4285F4" d="M21.35 12.22c0-.68-.06-1.34-.18-1.97H12v3.74h5.24a4.48 4.48 0 0 1-1.94 2.94v2.45h3.14c1.85-1.71 2.91-4.23 2.91-7.16Z" />
                      <path fill="#34A853" d="M12 21.75c2.63 0 4.84-.87 6.44-2.37l-3.14-2.45c-.87.59-1.98.94-3.3.94-2.54 0-4.7-1.71-5.47-4.02H3.29v2.52A9.74 9.74 0 0 0 12 21.75Z" />
                      <path fill="#FBBC05" d="M6.53 13.85a5.86 5.86 0 0 1 0-3.7V7.63H3.29a9.75 9.75 0 0 0 0 8.74l3.24-2.52Z" />
                      <path fill="#EA4335" d="M12 6.13c1.43 0 2.71.49 3.72 1.46l2.8-2.8A9.32 9.32 0 0 0 12 2.25a9.74 9.74 0 0 0-8.71 5.38l3.24 2.52C7.3 7.84 9.46 6.13 12 6.13Z" />
                    </svg>
                    <span>Sign in with Google</span>
                  </button>
                  <button
                    type="button"
                    className={styles.ssoButton}
                    onClick={() => loginMicrosoft.mutate()}
                    disabled={loginMicrosoft.isLoading}
                  >
                    <svg aria-hidden="true" viewBox="0 0 24 24" width="19" height="19">
                      <path fill="#F35325" d="M1 1h10v10H1z" />
                      <path fill="#81BC06" d="M13 1h10v10H13z" />
                      <path fill="#05A6F0" d="M1 13h10v10H1z" />
                      <path fill="#FFBA08" d="M13 13h10v10H13z" />
                    </svg>
                    <span>
                      {loginMicrosoft.isLoading ? "Redirecting..." : "Sign in with Microsoft"}
                    </span>
                  </button>
                </div>

              
                <div className={styles.signUpText}>
                  New user? <span>Sign Up</span>
                </div> 
              </div>

              <div className={styles.loginFooter}>
                By signing in, you agree to our{" "}
                <span onClick={() => loadDoc(2, setSaasContent, setShowSAASModal)}>
                  Terms of Service
                </span>{" "}
                and{" "}
                <span
                  onClick={() =>
                    loadDoc(3, setPrivacyContent, setShowPrivacyModal)
                  }
                >
                  Privacy Policy
                </span>
                <br />© 2026 Medicube Health — Smart Consent Platform
              </div>
            </div>
          </section>
        </div>
      </main>

      <PrivacyPolicyModal
        open={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        content={privacyContent}
      />

      <SaasModal
        open={showSAASModal}
        onClose={() => setShowSAASModal(false)}
        content={saasContent}
      />

      <EulaModal
        open={showEulaModal}
        onClose={() => setShowEulaModal(false)}
        content={eulaContent}
      />
    </div>
  );
}
